<template>
  <div id="app-wrap">
    <ModalWindow/>    
    <Site-Header/>
    <MobileMenu/>
    <main>
      <div :class="'outer-main-wrap ' + (uiStore.darkMode ? 'dark-mode' : 'light-mode')">
        <DarkModeToggle/>
        <NuxtPage />
        <FooterMenu/>
        <BackToTop/>
      </div>
    </main>
    <Site-Footer/>
    <LiveSales/>
    <CookiePopup/>
    <LiveChat/>
    <Popup/>
  </div>
</template>

<script setup lang="ts">
import '@fortawesome/fontawesome-free/css/all.css'
import { useUIStore } from '~/store/uiStore';
import { useDataStore } from '~/store/dataStore';
import { useUserStore } from '@/store/userStore';
import { onMounted, onBeforeUnmount, computed, watch } from 'vue';
import APIClient from '~/api/apiClient';
import { ModalTypes } from '@/types/enums';
import { useCartStore } from '~/store/cartStore';
import useCheckout from '~/composables/useCheckout';
import useBuyNow from '~/composables/useBuyNow';
import { useHead } from '@vueuse/head'
import { useLogoutAndClearCart } from '~/composables/useLogout';

const { logoutAndClearCart } = useLogoutAndClearCart();

const uiStore = useUIStore();
const dataStore = useDataStore();
const userStore = useUserStore();
const cartStore = useCartStore();

const isLoggedIn =  computed(() => userStore.userData?.is_logged_in || false);

watch(isLoggedIn, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    console.log(`isLoggedIn status changed from ${oldValue} to ${newValue} in app.vue`);
    dataStore.fetchProductData(true);
  }
});

const { reset: resetCheckout } = useCheckout();
const { reset: resetBuyNow } = useBuyNow();

watch(() => uiStore.modalType, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    // Do something when the modalType changes
    console.log(`Modal type changed from ${oldValue} to ${newValue}`);

    // If modal is set to INACTIVE, reset both checkout and buyNow
    if (newValue === ModalTypes.INACTIVE) {
      console.log("Modal type set to INACTIVE, resetting checkout and buyNow");
      resetCheckout();
      resetBuyNow();
    }
  }
});

const loadingScreenActive = uiStore.loadingScreenActive;

// If you want to perform any side effects when the value changes:
watch(
  () => uiStore.loadingScreenActive, // someObject is a hypothetical object in your store
  (newVal, oldVal) => {
    console.log('Loading Screen, The object or one of its nested properties changed to ', newVal );
  },
  { deep: true }
);

const updateScroll = () => {
  window.scrollTo(0, 0);
}

const darkMode = uiStore.darkMode; 

const toggleDarkMode = () => {
  uiStore.toggleDarkMode();
};

const apiClient = APIClient.getInstance();
  
const processAccessToken = async () => {
  try {
    const response = await apiClient.generateToken();
 
    if (response && response.valid) {
      console.log("Token generation successful");
 
    if (response && response.valid) { 
      console.log('Update session succesfully!', response.result.user);
      userStore.setUserData(response.result.user);
    }
    }
  
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

const updateAccessToken = async () => {
  console.log("Updating session...");
  try {
    console.log("User data:", userStore.userData);
    if(!userStore.userData || !userStore.userData.email_address)  return;
    const response = await apiClient.updateSession(userStore.userData.email_address);
    console.log("Update session response:", response);
 
    if (response && response.valid) {
      console.log('Update session succesfully!', response.result.user);
      userStore.setUserData(response.result.user);
    }
  
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

let refreshTokenTimer: number;
let sessionTimer: number;

const startSessionTimer = () => {
  if (!process.client) return;  // Return immediately if not on the client side

  // If there's already a session timer running, clear it
  if (sessionTimer) clearTimeout(sessionTimer);

  // Set the session timer
  sessionTimer = window.setTimeout(async () => {
    if(!isLoggedIn.value) return;
    console.log("Max session time reached. Ending the session...");
    await logoutAndClearCart();
    uiStore.setModalType(ModalTypes.TIMEOUT);
  }, apiClient.getTokenExpireTimeMs());
};

const startRefreshTokenTimer = () => {
  if (!process.client) return;  // Return immediately if not on the client side

  if (refreshTokenTimer) clearInterval(refreshTokenTimer);
  refreshTokenTimer = window.setInterval(processAccessToken, 5 * 60 * 1000);
};


let lastCalledTime: number = 0;

const handleUserInteraction = () => {
  const now = Date.now();
  
  // Check if it's been more than a minute since the last call
  if (now - lastCalledTime > 60 * 1000) {
    console.log("User interaction detected, processing access token...");
    updateAccessToken();
    startRefreshTokenTimer();
    
    lastCalledTime = now;

    // Reset the session timer on user interaction
    startSessionTimer();
  }
};

// When initializing your app or user logs in
startSessionTimer();

onMounted(async () => {

  processAccessToken();

  const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (prefersDarkMode !== darkMode) {
    console.log("Browser prefers dark mode, switching to dark mode");
    toggleDarkMode();
  }
  console.log("Fetching data... in App.vue");
  await dataStore.fetchProductData();
  userStore.determinePreferredCurrencyFromBrowser();

  cartStore.fetchCart();

  startRefreshTokenTimer();
  
  if (process.client) {
    // Using process.client to ensure this code only runs on the client side
    window.addEventListener('click', handleUserInteraction);
    window.addEventListener('mousemove', handleUserInteraction);
    window.addEventListener('keydown', handleUserInteraction);
  }
});

onBeforeUnmount(() => {
  if (process.client) {
    window.removeEventListener('click', handleUserInteraction);
    window.removeEventListener('mousemove', handleUserInteraction);
    window.removeEventListener('keydown', handleUserInteraction);
  }
});
useHead({
  htmlAttrs: {
    lang: 'en',
  },
  title: 'Forward Audio - Make A Difference with Premium Audio Plugins & Quality Guitar Cab IRs',
  meta: [
    { 
      name: 'description', 
      content: 'At Forward Audio, we strive to Make A Difference in your music production with premium guitar cab IRs and professional audio plugins. Elevate your sound today.' 
    },
    { 
      property: 'og:type', 
      content: 'website' 
    },
    { 
      property: 'og:title', 
      content: 'Forward Audio - Make A Difference' 
    },
    { 
      property: 'og:description', 
      content: 'Discover Forward Audio\'s premium guitar cab IRs and make a difference in your music production. Quality sound with professional audio tools tailored for you.' 
    },
    { 
      property: 'og:url', 
      content: 'https://www.forward-audio.com' 
    },
    { 
      property: 'og:image', 
      content: 'https://wp.forward-audio.com/wp-content/uploads/2023/10/2023-10-17-01-faTotalBundle-Box.jpg' 
    },
    { 
      property: 'og:site_name', 
      content: 'Forward Audio' 
    },
    { 
      name: 'twitter:card', 
      content: 'summary_large_image' 
    },
    { 
      name: 'twitter:title', 
      content: 'Forward Audio - Guitar Cab IRs that Make A Difference' 
    },
    { 
      name: 'twitter:description', 
      content: 'Join the Forward Audio family and make a difference with our guitar cab IRs and audio plugins. Quality sound is just a click away.' 
    },
    { 
      name: 'twitter:image', 
      content: 'https://wp.forward-audio.com/wp-content/uploads/2023/10/2023-10-17-01-faTotalBundle-Box.jpg' 
    },
  ],
  // Additional elements like canonical, robots, etc., can be added here.
})

</script>

<style lang="scss">
@import '~/assets/global.scss';
</style>
